/**
 * Countries: recognising the names a product sends, and what we know about each one — its two-letter
 * code (for its flag) and its UNAIDS region.
 *
 * Names are compared after folding case, accents and punctuation away, and every country carries the
 * other names it goes by: the UN's long form ("Democratic Republic of the Congo"), an older one
 * ("Swaziland"), a plain one ("Ivory Coast") and the short form the world map's shapes use
 * ("Dem. Rep. Congo"). A name that still does not match is never guessed at: the map lists it as
 * "not on the map", and the regional views put it under "Other".
 *
 * REGIONS ARE UNAIDS'S EIGHT, because this is where HIV financing is reported by region. A product
 * that sends a region of its own (a "Region" column) is believed over this table.
 */
export const fold = (s) => String(s ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim()

/** The eight UNAIDS regions. Each has a fixed colour, so a region keeps its colour on every chart
 *  whatever else is shown with it. The eight were checked together (lightness, chroma, colour-blind
 *  separation of neighbours); eight hues can never all be told apart by colour alone, so every view
 *  that colours by region also prints the region's name. */
export const REGIONS = {
  ESA: { name: 'Eastern & Southern Africa', short: 'E. & S. Africa', color: '#E31837' },
  WCA: { name: 'Western & Central Africa', short: 'W. & C. Africa', color: '#00A99A' },
  AP: { name: 'Asia & the Pacific', short: 'Asia-Pacific', color: '#2563EB' },
  LA: { name: 'Latin America', short: 'Latin America', color: '#F59E0B' },
  EECA: { name: 'Eastern Europe & Central Asia', short: 'E. Europe & C. Asia', color: '#92400E' },
  MENA: { name: 'Middle East & North Africa', short: 'M. East & N. Africa', color: '#65A30D' },
  CAR: { name: 'Caribbean', short: 'Caribbean', color: '#DB2777' },
  WCENA: { name: 'Western & Central Europe & North America', short: 'W. Europe & N. America', color: '#6D28D9' },
}
export const OTHER_REGION = { key: 'OTHER', name: 'Other', short: 'Other', color: '#9CA3AF' }

// code name;other name;… per country, by UNAIDS region.
const TABLE = {
  ESA: "AO Angola|BW Botswana|KM Comoros|ER Eritrea|SZ Eswatini;Swaziland;eSwatini|ET Ethiopia|KE Kenya|LS Lesotho|MG Madagascar|MW Malawi|MU Mauritius|MZ Mozambique|NA Namibia|RW Rwanda|SC Seychelles|ZA South Africa|SS South Sudan;S. Sudan|UG Uganda|TZ Tanzania;United Republic of Tanzania;Tanzania, United Republic of|ZM Zambia|ZW Zimbabwe",
  WCA: "BJ Benin|BF Burkina Faso|BI Burundi|CV Cabo Verde;Cape Verde|CM Cameroon|CF Central African Republic;Central African Rep.|TD Chad|CG Congo;Republic of the Congo;Republic of Congo;Congo-Brazzaville;Congo, Rep.|CI Côte d'Ivoire;Ivory Coast|CD Democratic Republic of the Congo;Democratic Republic of Congo;Dem. Rep. Congo;DR Congo;DRC;Congo, Dem. Rep.;Congo-Kinshasa|GQ Equatorial Guinea;Eq. Guinea|GA Gabon|GM Gambia;The Gambia;Gambia, The|GH Ghana|GN Guinea|GW Guinea-Bissau|LR Liberia|ML Mali|MR Mauritania|NE Niger|NG Nigeria|ST Sao Tome and Principe|SN Senegal|SL Sierra Leone|TG Togo",
  AP: "AF Afghanistan|AU Australia|BD Bangladesh|BT Bhutan|BN Brunei;Brunei Darussalam|KH Cambodia|CN China|KP North Korea;Democratic People's Republic of Korea;DPRK;Korea, Dem. People's Rep.|FJ Fiji|IN India|ID Indonesia|JP Japan|KI Kiribati|LA Laos;Lao PDR;Lao People's Democratic Republic|MY Malaysia|MV Maldives|MH Marshall Islands|FM Micronesia|MN Mongolia|MM Myanmar;Burma|NR Nauru|NP Nepal|NZ New Zealand|PK Pakistan|PW Palau|PG Papua New Guinea|PH Philippines|KR South Korea;Republic of Korea;Korea, Rep.;Korea|WS Samoa|SG Singapore|SB Solomon Islands;Solomon Is.|LK Sri Lanka|TH Thailand|TL Timor-Leste;East Timor|TO Tonga|TV Tuvalu|VU Vanuatu|VN Viet Nam;Vietnam|TW Taiwan|NC New Caledonia",
  LA: "AR Argentina|BO Bolivia;Bolivia (Plurinational State of)|BR Brazil|CL Chile|CO Colombia|CR Costa Rica|EC Ecuador|SV El Salvador|GT Guatemala|HN Honduras|MX Mexico|NI Nicaragua|PA Panama|PY Paraguay|PE Peru|UY Uruguay|VE Venezuela;Venezuela (Bolivarian Republic of)",
  CAR: "AG Antigua and Barbuda|BS Bahamas;The Bahamas|BB Barbados|BZ Belize|CU Cuba|DM Dominica|DO Dominican Republic;Dominican Rep.|GD Grenada|GY Guyana|HT Haiti|JM Jamaica|KN Saint Kitts and Nevis;St Kitts and Nevis|LC Saint Lucia;St Lucia|VC Saint Vincent and the Grenadines;St Vincent and the Grenadines|SR Suriname|TT Trinidad and Tobago|PR Puerto Rico",
  EECA: "AL Albania|AM Armenia|AZ Azerbaijan|BY Belarus|BA Bosnia and Herzegovina;Bosnia and Herz.|GE Georgia|KZ Kazakhstan|KG Kyrgyzstan;Kyrgyz Republic|ME Montenegro|MK North Macedonia;Macedonia;Republic of North Macedonia|MD Moldova;Republic of Moldova;Moldova, Republic of|RU Russia;Russian Federation|RS Serbia|TJ Tajikistan|TM Turkmenistan|UA Ukraine|UZ Uzbekistan|XK Kosovo",
  MENA: "DZ Algeria|BH Bahrain|DJ Djibouti|EG Egypt|IR Iran;Iran (Islamic Republic of);Iran, Islamic Republic of|IQ Iraq|JO Jordan|KW Kuwait|LB Lebanon|LY Libya|MA Morocco|OM Oman|QA Qatar|SA Saudi Arabia|SO Somalia|SD Sudan|SY Syria;Syrian Arab Republic|TN Tunisia|AE United Arab Emirates;UAE|YE Yemen|PS Palestine;State of Palestine;Palestine, State of|EH Western Sahara;W. Sahara",
  WCENA: "AD Andorra|AT Austria|BE Belgium|BG Bulgaria|CA Canada|HR Croatia|CY Cyprus|CZ Czechia;Czech Republic|DK Denmark|EE Estonia|FI Finland|FR France|DE Germany|GR Greece|HU Hungary|IS Iceland|IE Ireland|IL Israel|IT Italy|LV Latvia|LI Liechtenstein|LT Lithuania|LU Luxembourg|MT Malta|MC Monaco|NL Netherlands;The Netherlands|NO Norway|PL Poland|PT Portugal|RO Romania|SM San Marino|SK Slovakia;Slovak Republic|SI Slovenia|ES Spain|SE Sweden|CH Switzerland|TR Turkey;Türkiye|GB United Kingdom;UK;U.K.;Great Britain;United Kingdom of Great Britain and Northern Ireland|US United States;United States of America;USA;U.S.;US|GL Greenland",
}

const INDEX = {}      // folded name -> country
Object.entries(TABLE).forEach(([region, list]) => {
  list.split('|').forEach(entry => {
    const code = entry.slice(0, 2)
    const names = entry.slice(3).split(';')
    const country = { iso2: code, name: names[0], region, names }
    names.forEach(n => { INDEX[fold(n)] = country })
  })
})

/** {iso2, name, region, names} for a country name in any of its forms, or null. */
export const countryInfo = (name) => INDEX[fold(name)] || null

let atlasNames = null   // folded map name -> map name, filled once the atlas has loaded

/** Tells this module the names on the map. Called by the map once it has its shapes. */
export function registerAtlasNames(names) {
  atlasNames = {}
  names.forEach(n => { atlasNames[fold(n)] = n })
}

/** The map's name for this country, or null. */
export function atlasName(name) {
  if (!atlasNames) return null
  const f = fold(name)
  if (!f) return null
  if (atlasNames[f]) return atlasNames[f]
  const c = INDEX[f]
  if (!c) return null
  for (const n of c.names) if (atlasNames[fold(n)]) return atlasNames[fold(n)]
  return null
}

/** Whether these labels are a list of countries: at least `min` of them, and nearly all
 *  recognised. A list of regions, months or answers is not, so it never gets a map. */
export function looksLikeCountries(labels, min = 4) {
  const names = (labels || []).map(String).filter(s => s.trim())
  if (names.length < min) return false
  const hits = names.filter(n => INDEX[fold(n)]).length
  return hits >= min && hits / names.length >= 0.75
}

// A region a product names in a column of its own, in the forms it is likely to use.
const REGION_NAMES = {
  ESA: ['eastern and southern africa', 'east and southern africa', 'eastern southern africa', 'esa'],
  WCA: ['western and central africa', 'west and central africa', 'western central africa', 'wca'],
  AP: ['asia and the pacific', 'asia and pacific', 'asia pacific', 'ap', 'asap'],
  LA: ['latin america', 'la'],
  CAR: ['caribbean', 'car', 'the caribbean'],
  EECA: ['eastern europe and central asia', 'eeca'],
  MENA: ['middle east and north africa', 'mena'],
  WCENA: ['western and central europe and north america', 'wcena'],
}
const REGION_INDEX = {}
Object.entries(REGION_NAMES).forEach(([k, list]) => list.forEach(n => { REGION_INDEX[n] = k }))
Object.entries(REGIONS).forEach(([k, r]) => { REGION_INDEX[fold(r.name)] = k; REGION_INDEX[fold(r.short)] = k })
export const regionKeyOf = (text) => REGION_INDEX[fold(text)] || null

/** Countries grouped by region, largest region first and largest country first within each.
 *  `items` is [{name, value, region?}]; a `region` the product sent wins over the table's. Only
 *  positive figures count: a share of a whole needs something to share. */
export function groupByRegion(items) {
  const groups = {}
  const spare = ['#0284C7', '#B45309', '#15803D', '#BE185D']   // for regions the product names itself
  items.forEach(it => {
    const v = Number(it.value)
    if (!(v > 0)) return
    const info = countryInfo(it.name)
    let key = null, meta = null
    if (it.region) {
      key = regionKeyOf(it.region)
      if (!key) {
        key = `x:${fold(it.region)}`
        meta = { name: String(it.region), short: String(it.region) }
      }
    } else if (info) key = info.region
    if (!key) { key = OTHER_REGION.key; meta = OTHER_REGION }
    if (!groups[key]) {
      const base = REGIONS[key] || meta
      groups[key] = { key, name: base.name, short: base.short, color: base.color, total: 0, items: [] }
    }
    groups[key].items.push({ name: String(it.name), value: v, iso2: info?.iso2 || null, region: key })
    groups[key].total += v
  })
  const out = Object.values(groups).sort((a, b) => (a.key === 'OTHER') - (b.key === 'OTHER') || b.total - a.total)
  let s = 0
  out.forEach(g => {
    g.items.sort((a, b) => b.value - a.value)
    if (!g.color) g.color = spare[s++ % spare.length]
  })
  return out
}
